import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

// Dev-only debug logger — silent in production builds. Errors still use console.error.
const dlog = (...args: unknown[]) => { if (import.meta.env.DEV) console.log(...args); };

/**
 * Hook to track the app version (git commit) that each user is running.
 * Reports version info to the database on login to help identify users
 * with stale PWA cache.
 */
// Throttle: the upsert used to fire on every page load / PWA cold start /
// SIGNED_IN re-emission — ~70K writes, each paying an UPDATE trigger plus two
// index updates, for a row that almost never changed. Skip when this device
// already reported the same build within the last 24h. A new build's hash
// differs, so the Version Tracker still sees deploys immediately.
const REPORT_TTL_MS = 24 * 60 * 60 * 1000;
const reportedKey = (userId: string) => `app-version-reported:${userId}`;

const wasRecentlyReported = (userId: string, commitHash: string): boolean => {
  try {
    const raw = localStorage.getItem(reportedKey(userId));
    if (!raw) return false;
    const [hash, iso] = raw.split('|');
    return hash === commitHash && Date.now() - new Date(iso).getTime() < REPORT_TTL_MS;
  } catch {
    return false;
  }
};

const markReported = (userId: string, commitHash: string): void => {
  try {
    localStorage.setItem(reportedKey(userId), `${commitHash}|${new Date().toISOString()}`);
  } catch {
    // Storage unavailable (private mode) — fall back to unthrottled behavior.
  }
};

export function useAppVersionTracker() {
  const hasReported = useRef(false);

  useEffect(() => {
    const reportVersion = async (userId: string) => {
      // Only report once per session
      if (hasReported.current) return;
      hasReported.current = true;

      const commitHash = import.meta.env.VITE_GIT_COMMIT_HASH || 'unknown';
      if (wasRecentlyReported(userId, commitHash)) {
        dlog('[AppVersionTracker] Skipping report — same build reported within 24h');
        return;
      }
      const commitDate = import.meta.env.VITE_GIT_COMMIT_DATE || 'unknown';
      const commitMessage = import.meta.env.VITE_GIT_COMMIT_MESSAGE || 'unknown';
      const userAgent = navigator.userAgent;

      dlog('[AppVersionTracker] Attempting to report version:', {
        userId,
        commitHash,
        commitDate,
        commitMessage: commitMessage.substring(0, 50) + '...',
      });

      try {
        // Upsert the version info - update if exists, insert if not
        const { data, error } = await supabase
          .from('user_app_versions')
          .upsert({
            user_id: userId,
            commit_hash: commitHash,
            commit_date: commitDate,
            commit_message: commitMessage,
            last_login_at: new Date().toISOString(),
            user_agent: userAgent,
          }, {
            onConflict: 'user_id',
          });

        if (error) {
          console.error('[AppVersionTracker] Failed to report version:', {
            error: error.message,
            code: error.code,
            details: error.details,
            hint: error.hint,
          });
        } else {
          markReported(userId, commitHash);
          dlog('[AppVersionTracker] Successfully reported version:', commitHash);
        }
      } catch (err) {
        console.error('[AppVersionTracker] Exception reporting version:', err);
      }
    };

    // Check if user is already logged in
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        reportVersion(session.user.id);
      }
    });

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        // Reset the flag on new sign in (different session)
        hasReported.current = false;
        reportVersion(session.user.id);
      } else if (event === 'SIGNED_OUT') {
        // Reset the flag when user signs out
        hasReported.current = false;
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);
}

/**
 * Get current app version info for display
 */
export function getAppVersionInfo() {
  return {
    commitHash: import.meta.env.VITE_GIT_COMMIT_HASH || 'unknown',
    commitDate: import.meta.env.VITE_GIT_COMMIT_DATE || 'unknown',
    commitMessage: import.meta.env.VITE_GIT_COMMIT_MESSAGE || 'unknown',
  };
}
